<template>
  <div class="settings-page">
    <h2>Plugins</h2>
    <div class="subtitle">
      Supercharge your experience on desktop through platform-specific plugins.
    </div>
    <div class="plugins">
      <div class="plugin">
        <img :src="dashboardPlugin.asset.small" :alt="dashboardPlugin.name" />
        <div class="content">
          <div class="title">
            <h3>{{ dashboardPlugin.name }}</h3>
            <div v-if="in_electron" class="status-pill ready">
              <span class="status-dot" />
              <span>Enabled</span>
            </div>
          </div>
          <p>
            Open the dashboard anywhere on your desktop through the hotkey to
            quickly capture information.
          </p>
          <div class="footer">
            <div class="alert-container" v-if="!in_electron">
              <DownloadButton
                kind="main"
                type="secondary"
                text="Enable"
                message="The dashboard plugin is only supported in the Desktop apps"
              />
            </div>
            <div v-else>
              <BaseButton
                kind="main"
                type="primary"
                text="Enabled"
                state="disabled"
                tooltip="The dashboard plugin is always enabled on Desktop."
                tooltip-display="always"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="plugin">
        <img :src="mcpImage" alt="MCP" />
        <div class="content">
          <div class="title">
            <h3>MCP Server</h3>
            <div v-if="in_electron" class="status-pill" :class="mcpStatus.kind">
              <span class="status-dot" />
              <span>{{ mcpStatus.label }}</span>
            </div>
          </div>
          <p>
            Enable the MCP server to let your favorite AI app interact with
            Routine.
          </p>
          <p v-if="mcpStatus.message" class="status-message">
            {{ mcpStatus.message }}
          </p>
          <div class="footer">
            <div class="alert-container" v-if="!in_electron">
              <DownloadButton
                kind="main"
                type="secondary"
                text="Enable"
                message="The MCP server plugin is only supported in Desktop apps."
              />
            </div>
            <div v-else>
              <div class="mcp-actions">
                <UpgradeButton
                  entitlement-key="mcp"
                  :entitled="isMcpServerEnabled ? true : undefined"
                  kind="main"
                  :type="isMcpServerEnabled ? 'primary' : 'secondary'"
                  :text="isMcpServerEnabled ? 'Disable' : 'Enable'"
                  :tint="isMcpServerEnabled ? 'red' : undefined"
                  :state="mcpTogglePending ? 'loading' : 'default'"
                  @click="toggleMcpServer(!isMcpServerEnabled)"
                />
                <BaseButton
                  v-if="isMcpServerEnabled"
                  kind="main"
                  type="secondary"
                  text="Setup"
                  @click="openMcpSetup()"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="plugin">
        <img :src="menubarPlugin.asset.small" :alt="menubarPlugin.name" />
        <div class="content">
          <div class="title">
            <h3>{{ menubarPlugin.name }}</h3>
            <div
              v-if="in_electron && isMenuBarEnabled"
              class="status-pill ready"
            >
              <span class="status-dot" />
              <span>Enabled</span>
            </div>
          </div>
          <p>
            Never miss a meeting again, access your tasks & events of the day,
            run timers (e.g Pomodoro) and record meetings with AI.
          </p>
          <div class="footer">
            <div class="alert-container" v-if="!in_electron">
              <DownloadButton
                kind="main"
                type="secondary"
                text="Enable"
                message="The menu bar widget plugin is only supported in the Desktop apps"
              />
            </div>
            <div v-else>
              <!--
                On the plugin toggle buttons below, `entitled` is forced to
                true while the plugin is enabled: disabling must always be
                possible, even after the entitlement was lost. Only enabling
                (`undefined` defers to the billing entitlement) is gated.
              -->
              <UpgradeButton
                entitlement-key="menu-bar-widget"
                :entitled="isMenuBarEnabled ? true : undefined"
                kind="main"
                :type="isMenuBarEnabled ? 'primary' : 'secondary'"
                :text="isMenuBarEnabled ? 'Disable' : 'Enable'"
                :tint="isMenuBarEnabled ? 'red' : undefined"
                @click="toggleMenuBar(!isMenuBarEnabled)"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="plugin">
        <img :src="capturePlugin.asset.small" :alt="capturePlugin.name" />
        <div class="content">
          <div class="title">
            <h3>{{ capturePlugin.name }}</h3>
            <div
              v-if="isOnMacOSDesktop && isContextualCaptureEnabled"
              class="status-pill ready"
            >
              <span class="status-dot" />
              <span>Enabled</span>
            </div>
          </div>
          <p>
            Capture information along with its context from anywhere (browser,
            app etc.).
          </p>
          <div class="footer">
            <div>
              <div class="alert-container" v-if="!isOnMacOSDesktop">
                <DownloadButton
                  kind="main"
                  type="secondary"
                  text="Enable"
                  :platforms="['macOS']"
                  message="The contextual capture plugin is only supported on macOS."
                />
              </div>
              <div v-else>
                <UpgradeButton
                  entitlement-key="contextual-capture"
                  :entitled="isContextualCaptureEnabled ? true : undefined"
                  kind="main"
                  :type="isContextualCaptureEnabled ? 'primary' : 'secondary'"
                  :text="isContextualCaptureEnabled ? 'Disable' : 'Enable'"
                  :tint="isContextualCaptureEnabled ? 'red' : undefined"
                  @click="toggleContextualCapture(!isContextualCaptureEnabled)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  h,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useModal } from "vue-final-modal";

import BaseButton from "@/components/base/BaseButton.vue";
import DownloadButton from "@/components/base/DownloadButton.vue";
import UpgradeButton from "@/components/base/UpgradeButton.vue";
import * as inject from "@/components/inject";
import Modal from "@/components/modals/Modal.vue";
import type { ModalButtons } from "@/components/modals/Modal.vue";
import McpSetupModal from "@/components/settings/McpSetupModal.vue";
import {
  checkingMcpPluginStatus,
  getMcpSetup,
  getMcpStatus,
  mcpPluginStatus,
  setMcpEnabled,
  type McpPluginStatus,
} from "@/mcp/config";
import { metrics } from "@/metrics";
import { in_electron, in_mac } from "@/utils/in";
import { navigateToPlan } from "@/utils/navigate";
import { openExternalUrl } from "@/utils/open-external-url";
import { getPlugin } from "@/utils/plugins";
import { errorMessage } from "@/utils/unknown";

export default defineComponent({
  components: {
    BaseButton,
    DownloadButton,
    UpgradeButton,
  },
  async setup() {
    const controller = inject.controller();
    const router = inject.router();
    const profile = inject.profile();
    const billing = inject.billing();
    const isMcpServerEnabled = inject.isMcpServerEnabled();
    const isContextualCaptureEnabledSetting = ref(
      inject.isContextualCaptureEnabled()
    );

    /*
     * TODO: the entitlement checks are performed when enabling the plugins which in turn
     * saves a value in the local storage.
     *
     * This is obviously not very safe. We would need to check the entitlement at the
     * launch of the plugins instead.
     */
    const menuBarEntitlementRef = computed(() => {
      return billing.value?.isEntitledTo("menu-bar-widget");
    });
    const contextualCaptureEntitlementRef = computed(() => {
      return billing.value?.isEntitledTo("contextual-capture");
    });
    const isEntitledToMenuBar = computed(() => {
      return menuBarEntitlementRef.value?.value ?? false;
    });
    const isEntitledToContextualCapture = computed(() => {
      return contextualCaptureEntitlementRef.value?.value ?? false;
    });
    // Reflect the real stored state regardless of the entitlement: if the
    // entitlement is lost while a plugin is on, the button must keep showing
    // "Disable" so the user can still turn it off (only enabling is gated).
    const refreshMenuBarState = async () => {
      if (in_electron) {
        isMenuBarEnabled.value =
          await require("electron").ipcRenderer.invoke("get-menubar-state");
      }
    };

    onMounted(refreshMenuBarState);
    // A live revocation force-disables the widget (see Routine.vue); refresh
    // so the button reflects it.
    watch(isEntitledToMenuBar, () => void refreshMenuBarState());

    const isContextualCaptureEnabled = computed(
      () => isContextualCaptureEnabledSetting.value
    );
    const isMenuBarEnabled = ref(false);

    const mcpTogglePending = ref(false);
    const mcpStatus = ref<McpPluginStatus>(checkingMcpPluginStatus);

    const isOnMacOSDesktop = computed(() => in_electron && in_mac());

    const openUrl = (url: string) => openExternalUrl(url, controller);
    const dashboardPlugin = getPlugin("dashboard")!;
    const menubarPlugin = getPlugin("menubar-widget")!;
    const capturePlugin = getPlugin("contextual-capture")!;
    const mcpImage = new URL("../../assets/settings/mcp.png", import.meta.url)
      .href;

    const goToPlan = () => navigateToPlan(router, profile.value.user);
    const mcpSetupModalButtons: ModalButtons = [
      {
        title: "Close",
        kind: "main",
        func: async () => {},
      },
    ];

    const sendMetrics = (plugin: string, enabled: boolean) => {
      switch (enabled) {
        case true:
          metrics.segment.track("Plugin Enabled", {
            action: "enabled",
            name: plugin,
          });
          break;
        case false:
          metrics.segment.track("Plugin Disabled", {
            action: "disabled",
            name: plugin,
          });
          break;
      }
    };

    let mcpRefreshId = 0;
    const refreshMcpStatus = async () => {
      // The status pill is desktop-only; on web the card shows the download
      // button instead.
      if (!in_electron) return;

      const refreshId = ++mcpRefreshId;
      try {
        const setup = await getMcpStatus();
        if (refreshId !== mcpRefreshId) return;
        isMcpServerEnabled.value = setup.enabled;
        mcpStatus.value = mcpPluginStatus(setup);
      } catch (error) {
        if (refreshId !== mcpRefreshId) return;
        mcpStatus.value = {
          kind: "error",
          label: "Unavailable",
          message: errorMessage(error),
        };
      }
    };

    let mcpStatusRefreshTimer: ReturnType<typeof setInterval> | null = null;
    const onMcpStatusChanged = () => void refreshMcpStatus();
    onMounted(() => {
      void refreshMcpStatus();
      if (in_electron) {
        const electron: typeof import("electron") = require("electron");
        electron.ipcRenderer.on("mcp-status-changed", onMcpStatusChanged);
        mcpStatusRefreshTimer = setInterval(onMcpStatusChanged, 5_000);
      }
    });
    onBeforeUnmount(() => {
      if (!in_electron) return;
      const electron: typeof import("electron") = require("electron");
      electron.ipcRenderer.removeListener(
        "mcp-status-changed",
        onMcpStatusChanged
      );
      if (mcpStatusRefreshTimer) clearInterval(mcpStatusRefreshTimer);
    });
    watch(isMcpServerEnabled, () => {
      void refreshMcpStatus();
    });

    const openMcpSetup = async () => {
      try {
        const modalSetup = await getMcpSetup();
        mcpStatus.value = mcpPluginStatus(modalSetup);
        const { open } = useModal({
          component: Modal,
          attrs: {
            title: "Set up MCP server",
            isCancellable: true,
            showCancelButton: false,
            modalButtons: mcpSetupModalButtons,
          },
          slots: {
            default: () => h(McpSetupModal, { setup: modalSetup }),
          },
        });
        void open();
      } catch (error) {
        controller.notifications.notify(errorMessage(error));
      }
    };

    const toggleContextualCapture = (value: boolean) => {
      // Disabling is never gated by the entitlement.
      if (
        in_electron &&
        in_mac() &&
        (!value || isEntitledToContextualCapture.value)
      ) {
        if (!value) {
          const exec = require("child_process").exec;
          exec("tccutil reset Accessibility co.routine.desktop");
          exec("tccutil reset AppleEvents co.routine.desktop");
        }
        isContextualCaptureEnabledSetting.value = value;
      }

      sendMetrics("contextual capture", value);
    };

    const toggleMcpServer = async (value: boolean) => {
      if (!in_electron || mcpTogglePending.value) return;

      mcpTogglePending.value = true;
      try {
        const setup = await setMcpEnabled(value);
        isMcpServerEnabled.value = setup.enabled;
        mcpStatus.value = mcpPluginStatus(setup);
        sendMetrics("mcp server", value);
        if (value) await openMcpSetup();
      } catch (error) {
        controller.notifications.notify(errorMessage(error));
        await refreshMcpStatus();
      } finally {
        mcpTogglePending.value = false;
      }
    };

    const toggleMenuBar = async (enabled: boolean) => {
      // Disabling is never gated by the entitlement.
      if (in_electron && (!enabled || isEntitledToMenuBar.value)) {
        require("electron").ipcRenderer.send("toggle-menubar", enabled);
        isMenuBarEnabled.value = enabled;
      }

      sendMetrics("menu bar wiget", enabled);
    };

    return {
      in_electron,
      dashboardPlugin,
      menubarPlugin,
      capturePlugin,
      mcpImage,
      isOnMacOSDesktop,
      isContextualCaptureEnabled,
      isMcpServerEnabled,
      isMenuBarEnabled,
      isEntitledToMenuBar,
      isEntitledToContextualCapture,
      mcpTogglePending,
      mcpStatus,
      openUrl,
      goToPlan,
      openMcpSetup,
      toggleContextualCapture,
      toggleMcpServer,
      toggleMenuBar,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.plugins {
  display: flex;
  flex-direction: column;
  width: 100%;

  .plugin {
    padding: 0;
    display: flex;
    flex-direction: row;
    column-gap: 24px;
    padding: 24px 0;
    @include themed() {
      border-bottom: 1px solid t($grey-10);
    }

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      border-bottom: none;
    }

    img {
      width: 179px;
      height: 136px;
      border-radius: $border-radius-m;
      object-fit: cover;
    }

    p {
      margin: 0;
      @extend %font-caption-regular;
      @include themed() {
        color: t($grey-60);
      }

      &.warning {
        margin-bottom: 8px;
      }
    }

    .content {
      display: flex;
      flex-direction: column;
      margin-bottom: 0px;

      .title {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        column-gap: 8px;
        margin-bottom: 8px;
        margin-top: 4px;
      }

      h3 {
        margin: 0;
        @extend %font-body-medium;
      }

      .premium {
        margin-inline-start: 8px;
      }
    }

    .alert-container {
      width: 100%;
    }

    .footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 16px;
    }

    .status-pill {
      display: inline-flex;
      align-items: center;
      column-gap: 6px;
      width: fit-content;
      border-radius: 999px;
      padding: 3px 8px;
      @extend %font-caption-medium;
      @include themed() {
        background-color: t($grey-05);
        color: t($grey-60);
      }

      .status-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background-color: currentColor;
      }

      &.ready {
        color: $green;
      }

      &.error {
        color: $red;
      }

      &.pending,
      &.checking {
        color: $starred;
      }
    }

    .status-message {
      margin-top: 8px;
      max-width: 420px;
      overflow-wrap: anywhere;
    }

    .mcp-actions {
      display: flex;
      align-items: center;
      column-gap: 8px;
      flex-shrink: 0;
    }
  }
}

.premium-icon {
  width: 14px;
  height: 14px;
  margin-bottom: 2px;
}
</style>
